import React from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import IntegrationCard from "./IntegrationCard";
import Tooltip from "@mui/material/Tooltip";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CircularProgress from "@mui/material/CircularProgress";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_store_pos_integration_summary } from "../../../constants";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

function IntegrationsTableRow(props) {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setPosIntegrationSummary(null);
    setPosSummaryError(null);
    setPosSummaryLoading(false);
  };

  const [integrations, setIntegrations] = React.useState(
    props.location?.integration_data || []
  );
  const [posIntegrationSummary, setPosIntegrationSummary] = React.useState(null);
  const [posSummaryLoading, setPosSummaryLoading] = React.useState(false);
  const [posSummaryError, setPosSummaryError] = React.useState(null);

  const handleDeleteIntegration = (index) => {
    const updatedIntegrations = [...integrations];
    updatedIntegrations.splice(index, 1);
    setIntegrations(updatedIntegrations);
  };

  function handleSubmit() {
    props.update_integrations_info(props.location?.id, integrations);
    handleClose();
  }

  React.useEffect(() => {
    setIntegrations(props.location?.integration_data || []);
  }, [props.location?.integration_data]);

  const posLinkInfo = props.posLinkMap?.[props.location?.id] || {};
  const hasPosLink = Boolean(posLinkInfo.hasPosLink);
  const linkedProviderLabel = posLinkInfo.providerLabel || "POS";
  const locationSuffix = posLinkInfo.locationName
    ? ` – ${posLinkInfo.locationName}`
    : "";
  const linkTooltip = hasPosLink
    ? `Linked to ${linkedProviderLabel}${locationSuffix}`
    : "No POS location linked yet";
  const linkIndicatorClasses = `inline-flex h-6 w-6 items-center justify-center rounded-full border ${
    hasPosLink
      ? "border-emerald-200 bg-emerald-50 text-emerald-600"
      : "border-slate-200 bg-slate-50 text-slate-400"
  }`;

  const formatDateDisplay = (value, withTime = false) => {
    if (!value) {
      return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const options = withTime
      ? {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }
      : { month: "short", day: "numeric", year: "numeric" };
    return date.toLocaleString(undefined, options);
  };

  const formatRange = (start, end, withTime = false) => {
    if (!start && !end) {
      return "No data";
    }
    if (start && end) {
      if (start === end) {
        return formatDateDisplay(start, withTime);
      }
      return `${formatDateDisplay(start, withTime)} – ${formatDateDisplay(
        end,
        withTime
      )}`;
    }
    return formatDateDisplay(start || end, withTime);
  };

  const formatCount = (value) => {
    const numericValue = Number(value || 0);
    return numericValue.toLocaleString();
  };

  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    return numericValue.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  };

  const formatHours = (value) => {
    const numericValue = Number(value || 0);
    return `${numericValue.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })} hrs`;
  };

  const formatTemperature = (value, { includeUnit = true } = {}) => {
    if (value === null || value === undefined) {
      return includeUnit ? "—" : "";
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return includeUnit ? "—" : "";
    }
    const formatted = numericValue.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
    return includeUnit ? `${formatted}°F` : formatted;
  };

  const formatPrecipitation = (value) => {
    if (value === null || value === undefined) {
      return "—";
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return "—";
    }
    return `${numericValue.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    })} in`;
  };

  const buildDailySeries = (summary, startKey, endKey, preferredValueKey = null) => {
    if (!summary) {
      return [];
    }

    const startDate = summary[startKey] ? new Date(summary[startKey]) : null;
    const endDate = summary[endKey] ? new Date(summary[endKey]) : null;

    if (!startDate || !endDate || Number.isNaN(startDate) || Number.isNaN(endDate)) {
      return [];
    }

    const rawDailyCounts =
      summary.daily_counts ||
      summary.records_by_day ||
      summary.counts_by_day ||
      summary.counts_by_date;

    const countsByDate = new Map();

    if (Array.isArray(rawDailyCounts)) {
      rawDailyCounts.forEach((entry) => {
        const dateKey =
          entry?.date || entry?.business_date || entry?.day || entry?.date_string;
        if (!dateKey) return;
        const trimmedDateKey = dateKey.slice(0, 10);
        const countValue =
          (preferredValueKey ? entry?.[preferredValueKey] : null) ??
          entry?.count ??
          entry?.value ??
          entry?.hours ??
          entry?.records ??
          entry?.record_count ??
          entry?.total ??
          entry?.amount_cents ??
          0;
        countsByDate.set(trimmedDateKey, Number(countValue) || 0);
      });
    } else if (rawDailyCounts && typeof rawDailyCounts === "object") {
      Object.entries(rawDailyCounts).forEach(([dateKey, value]) => {
        const trimmedDateKey = dateKey.slice(0, 10);
        countsByDate.set(trimmedDateKey, Number(value) || 0);
      });
    }

    if (countsByDate.size === 0 && startDate.getTime() === endDate.getTime()) {
      return [
        {
          date: startDate.toISOString().slice(0, 10),
          count: Number(summary.record_count) || 0,
          label: startDate.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
        },
      ];
    }

    const data = [];
    const cursor = new Date(startDate);
    while (cursor <= endDate) {
      const dateKey = cursor.toISOString().slice(0, 10);
      data.push({
        date: dateKey,
        count: countsByDate.get(dateKey) || 0,
        label: cursor.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return data;
  };

  const DailyRecordsChart = ({
    data,
    color,
    gradientId,
    valueLabel = "Records",
    valueFormatter = formatCount,
  }) => {
    if (!data?.length) {
      return (
        <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-500">
          Daily record details will appear here once data syncs.
        </div>
      );
    }

    return (
      <div className="h-32 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 6, right: 0, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                <stop offset="95%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" hide />
            <YAxis hide domain={[0, (dataMax) => Math.ceil(dataMax * 1.1) || 1]} />
            <RechartsTooltip
              cursor={{ stroke: color, strokeDasharray: "3 3" }}
              formatter={(value) => [valueFormatter(value), valueLabel]}
              labelFormatter={(label, payload) => payload?.[0]?.payload?.label || label}
              contentStyle={{
                borderRadius: "12px",
                border: "1px solid rgb(226 232 240)",
                boxShadow: "0 10px 25px rgba(15, 23, 42, 0.08)",
                fontSize: "12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke={color}
              fill={`url(#${gradientId})`}
              strokeWidth={2}
              fillOpacity={1}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const fetchPosIntegrationSummary = React.useCallback(async () => {
    if (!props.location?.id) {
      return;
    }
    setPosSummaryLoading(true);
    setPosSummaryError(null);
    try {
      const res = await getRightworkAPI(
        API_get_store_pos_integration_summary,
        { store_id: props.location.id }
      );
      const payload = res?.data?.redux?.payload || res?.data || {};
      setPosIntegrationSummary(payload);
    } catch (error) {
      setPosSummaryError("Unable to load integration details.");
      console.error("Error loading POS integration summary", error);
    } finally {
      setPosSummaryLoading(false);
    }
  }, [props.location?.id]);

  React.useEffect(() => {
    if (open && hasPosLink) {
      fetchPosIntegrationSummary();
    }
  }, [open, hasPosLink, fetchPosIntegrationSummary]);

  React.useEffect(() => {
    setPosIntegrationSummary(null);
    setPosSummaryError(null);
  }, [props.location?.id]);

  const renderManualIntegrationContent = () => (
    <div className="w-full px-6 pb-24">
      <div className="mt-5">
        <p className="text-slate-900 text-md font-semibold">Integrations</p>
        <p className="text-slate-500 text-sm mt-1">
          Configure the integrations for this location
        </p>
      </div>
      <div className="mt-5">
        {(integrations || []).map((integration, index) => {
          return (
            <div key={index} className="mt-3">
              <IntegrationCard
                integration={integration}
                onDelete={() => handleDeleteIntegration(index)}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
        <p className="text-sm font-medium text-slate-700">
          Manual integrations can no longer be added here.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Use the company-level integration flow to connect POS data.
        </p>
      </div>
    </div>
  );

  const renderPosIntegrationContent = () => {
    const summary = posIntegrationSummary || {};
    const salesSummary = summary.sales_entries || {};
    const timeSummary = summary.time_entries || {};
    const actualSalesDriversSummary = summary.actual_sales_drivers || {};
    const actualUsedLaborSummary = summary.actual_used_labor || {};
    const earnedLaborSummary = summary.earned_labor || {};
    const forecastedSalesDriversSummary = summary.forecasted_sales_drivers || {};
    const forecastedLaborSummary = summary.forecasted_labor || {};
    const weatherSummary = summary.weather || {};
    const providerLabel =
      linkedProviderLabel || summary.store?.pos_provider_label || "POS";
    const linkedLocationName =
      summary.pos_store?.location_name ||
      summary.store?.pos_details?.location_name ||
      posLinkInfo.locationName;

    return (
      <div className="w-full px-6 pb-24 space-y-6">
        <div className="mt-5">
          <p className="text-slate-900 text-md font-semibold">POS Integration</p>
          <p className="text-slate-500 text-sm mt-1">
            Linked to {providerLabel}
            {linkedLocationName ? ` – ${linkedLocationName}` : ""}
          </p>
        </div>
        {posSummaryLoading ? (
          <div className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </div>
        ) : posSummaryError ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {posSummaryError}
          </div>
        ) : (
          <>
            <div className="mt-5 space-y-4">
              <div className="border border-slate-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-slate-900">Sales sync</p>
                <p className="text-xs text-slate-500 mt-1">
                  POS tickets imported into Rightwork
                </p>
                <div className="mt-4 space-y-3 text-sm">
                  <DailyRecordsChart
                    data={buildDailySeries(
                      salesSummary,
                      "first_business_date",
                      "last_business_date"
                    )}
                    color="#6d28d9"
                    gradientId="salesRecordsGradient"
                  />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Business day range</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        salesSummary.first_business_date,
                        salesSummary.last_business_date
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Records synced</span>
                    <span className="text-slate-900 font-medium">
                      {formatCount(salesSummary.record_count)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last order received</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatDateDisplay(salesSummary.last_ordered_at, true)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-slate-900">Time entries</p>
                <p className="text-xs text-slate-500 mt-1">
                  Labor hours imported from the POS
                </p>
                <div className="mt-4 space-y-3 text-sm">
                  <DailyRecordsChart
                    data={buildDailySeries(
                      timeSummary,
                      "first_clocked_in_at",
                      "last_clocked_in_at"
                    )}
                    color="#0ea5e9"
                    gradientId="timeRecordsGradient"
                  />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Clock-in range</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        timeSummary.first_clocked_in_at,
                        timeSummary.last_clocked_in_at
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Records synced</span>
                    <span className="text-slate-900 font-medium">
                      {formatCount(timeSummary.record_count)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Most recent entry</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatDateDisplay(timeSummary.last_clocked_in_at, true)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-slate-900">
                  Actual sales drivers
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Daily sales totals from the POS
                </p>
                <div className="mt-4 space-y-3 text-sm">
                  <DailyRecordsChart
                    data={buildDailySeries(
                      actualSalesDriversSummary,
                      "first_date",
                      "last_date",
                      "value"
                    )}
                    color="#f97316"
                    gradientId="actualSalesDriversGradient"
                    valueLabel="Sales"
                    valueFormatter={(value) => formatCurrency(value)}
                  />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date range</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        actualSalesDriversSummary.first_date,
                        actualSalesDriversSummary.last_date
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total value</span>
                    <span className="text-slate-900 font-medium">
                      {formatCurrency(actualSalesDriversSummary.total_value)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Entries</span>
                    <span className="text-slate-900 font-medium">
                      {formatCount(actualSalesDriversSummary.record_count)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-slate-900">
                  Actual used labor
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Aggregated labor hours from time entries
                </p>
                <div className="mt-4 space-y-3 text-sm">
                  <DailyRecordsChart
                    data={buildDailySeries(
                      actualUsedLaborSummary,
                      "first_date",
                      "last_date",
                      "hours"
                    )}
                    color="#10b981"
                    gradientId="actualUsedLaborGradient"
                    valueLabel="Hours"
                    valueFormatter={(value) => formatHours(value)}
                  />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date range</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        actualUsedLaborSummary.first_date,
                        actualUsedLaborSummary.last_date
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total hours</span>
                    <span className="text-slate-900 font-medium">
                      {formatHours(actualUsedLaborSummary.total_hours)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Entries</span>
                    <span className="text-slate-900 font-medium">
                      {formatCount(actualUsedLaborSummary.record_count)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-slate-900">
                  Earned labor
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Labor model output calculated from actual sales and usage.
                </p>
                <div className="mt-4 space-y-3 text-sm">
                  <DailyRecordsChart
                    data={buildDailySeries(
                      earnedLaborSummary,
                      "first_date",
                      "last_date",
                      "hours"
                    )}
                    color="#d97706"
                    gradientId="earnedLaborGradient"
                    valueLabel="Hours"
                    valueFormatter={(value) => formatHours(value)}
                  />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date range</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        earnedLaborSummary.first_date,
                        earnedLaborSummary.last_date
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total hours</span>
                    <span className="text-slate-900 font-medium">
                      {formatHours(earnedLaborSummary.total_hours)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Entries</span>
                    <span className="text-slate-900 font-medium">
                      {formatCount(earnedLaborSummary.record_count)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-slate-900">Weather data</p>
                <p className="text-xs text-slate-500 mt-1">
                  Weather history + forecasts feeding staffing insights.
                </p>
                <div className="mt-4 space-y-3 text-sm">
                  <DailyRecordsChart
                    data={buildDailySeries(
                      weatherSummary,
                      "first_date",
                      "last_date",
                      "value"
                    )}
                    color="#0284c7"
                    gradientId="weatherRecordsGradient"
                    valueLabel="Avg temp"
                    valueFormatter={(value) => formatTemperature(value)}
                  />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date range</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        weatherSummary.first_date,
                        weatherSummary.last_date
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Records stored</span>
                    <span className="text-slate-900 font-medium">
                      {formatCount(weatherSummary.record_count)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last updated</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        weatherSummary.last_updated,
                        weatherSummary.last_updated
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Avg temperature</span>
                    <span className="text-slate-900 font-medium">
                      {formatTemperature(weatherSummary.average_temperature)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Avg precipitation</span>
                    <span className="text-slate-900 font-medium">
                      {formatPrecipitation(weatherSummary.average_precipitation)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-slate-900">
                  Forecasted sales drivers
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Forecast output by business week
                </p>
                <div className="mt-4 space-y-3 text-sm">
                  <DailyRecordsChart
                    data={buildDailySeries(
                      forecastedSalesDriversSummary,
                      "first_date",
                      "last_date",
                      "value"
                    )}
                    color="#3b82f6"
                    gradientId="forecastedSalesDriversGradient"
                    valueLabel="Sales"
                    valueFormatter={(value) => formatCurrency(value)}
                  />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date range</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        forecastedSalesDriversSummary.first_date,
                        forecastedSalesDriversSummary.last_date
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total value</span>
                    <span className="text-slate-900 font-medium">
                      {formatCurrency(forecastedSalesDriversSummary.total_value)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Entries</span>
                    <span className="text-slate-900 font-medium">
                      {formatCount(forecastedSalesDriversSummary.record_count)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-slate-900">
                  Forecasted labor
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Predicted labor demand generated from the current labor model.
                </p>
                <div className="mt-4 space-y-3 text-sm">
                  <DailyRecordsChart
                    data={buildDailySeries(
                      forecastedLaborSummary,
                      "first_date",
                      "last_date",
                      "hours"
                    )}
                    color="#4f46e5"
                    gradientId="forecastedLaborGradient"
                    valueLabel="Hours"
                    valueFormatter={(value) => formatHours(value)}
                  />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date range</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatRange(
                        forecastedLaborSummary.first_date,
                        forecastedLaborSummary.last_date
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total hours</span>
                    <span className="text-slate-900 font-medium">
                      {formatHours(forecastedLaborSummary.total_hours)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Entries</span>
                    <span className="text-slate-900 font-medium">
                      {formatCount(forecastedLaborSummary.record_count)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Backfills moved to onboarding
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Historical backfills can now only be started from the onboarding view for this location.
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderIntegrationCellContent = () => {
    if (hasPosLink) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
          {linkedProviderLabel} · Linked
        </span>
      );
    }

    if (props.location?.integration_data?.length > 0) {
      return (
        <div className="w-full flex flex-wrap items-center gap-2">
          {props.location?.integration_data.map((integration, index) => (
            <div
              key={index}
              className="rounded-md bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700"
            >
              {integration.provider}
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-xs text-slate-400">No integration data</span>;
  };

  const canOpenDrawer = hasPosLink || (props.location?.integration_data?.length > 0);

  return (
    <>
      <tr
        className={canOpenDrawer ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}
        onClick={canOpenDrawer ? handleOpen : undefined}
      >
        <td className="px-4 py-3 text-sm font-medium text-slate-800">
          <div className="flex items-center gap-2">
            <Tooltip title={linkTooltip} placement="top" arrow>
              <span className={linkIndicatorClasses}>
                {hasPosLink ? (
                  <LinkIcon fontSize="inherit" />
                ) : (
                  <LinkOffIcon fontSize="inherit" />
                )}
              </span>
            </Tooltip>
            <span>{props.location.store_name}</span>
          </div>
        </td>
        <td className="border-l border-slate-100 px-4 py-3 text-sm text-slate-900">
          {renderIntegrationCellContent()}
        </td>
        <td className="border-l border-slate-100 px-4 py-3 text-right">
          {canOpenDrawer ? (
            <button type="button" aria-label={`View integration details for ${props.location.store_name}`}
              onClick={(event) => { event.stopPropagation(); handleOpen(); }}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded text-xs font-medium text-violet-700 hover:text-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600">
              View details<ChevronRightIcon fontSize="small" />
            </button>
          ) : <span className="text-xs text-slate-300" aria-label="No details available">—</span>}
        </td>
      </tr>
      <Drawer open={open} anchor="right" onClose={handleClose} PaperProps={{ sx: { width: 450, maxWidth: "100vw" } }}>
        <div className="w-full h-full flex flex-col">
          <DrawerHeader title={props.location.store_name} onClose={handleClose} />
          <div className="flex-1 overflow-y-auto">
            {hasPosLink ? renderPosIntegrationContent() : renderManualIntegrationContent()}
          </div>
          <div>
            {hasPosLink ? null : (
              <DrawerFooterContainerLarge>
                <div className="w-full">
                  <LoadingButton
                    variant="contained"
                    loadingPosition="end"
                    endIcon={<SaveIcon />}
                    onClick={() => handleSubmit()}
                    sx={{
                      width: "100%",
                      textTransform: "none",
                      borderRadius: "12px",
                      paddingY: "14px",
                      backgroundColor: "#6d28d9",
                      "&:hover": {
                        backgroundColor: "#5b21b6",
                      },
                      fontSize: "16px",
                    }}
                  >
                    Save Changes
                  </LoadingButton>
                </div>
                <div className="w-full mt-3">
                  <ButtonOutlined text="Cancel" onClick={() => handleClose()} />
                </div>
              </DrawerFooterContainerLarge>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default IntegrationsTableRow;
